import { useEffect, useState } from "react";
import { toast } from "sonner";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import api, { apiDownload, formatApiError } from "../lib/api";
import {
  ShieldCheck,
  DownloadSimple,
  Trash,
  ArrowsClockwise,
  Database,
  Clock,
  Robot,
  Hand,
} from "@phosphor-icons/react";

const fmtSize = (n) => {
  if (!n) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const h = Math.floor(diff / 3600000);
  if (h < 1) return "meno di un'ora fa";
  if (h < 24) return `${h} ${h === 1 ? "ora" : "ore"} fa`;
  const g = Math.floor(h / 24);
  return `${g} ${g === 1 ? "giorno" : "giorni"} fa`;
};

const ETICHETTE = {
  utenti: "Utenti studio",
  pratiche: "Incarichi",
  clienti: "Clienti",
  imprese: "Imprese",
  tecnici: "Tecnici",
  tipi_incarico: "Tipi incarico",
  macroaree: "Macroaree",
  appuntamenti: "Appuntamenti",
  timesheet: "Ore lavorate",
  fatture: "Fatture",
  notule: "Avvisi di notula",
  preventivi: "Preventivi",
  computi: "Computi",
  computi_preventivi: "Computi & preventivi imprese",
  voci_lavorazioni: "Voci di lavorazione",
  categorie_voci: "Categorie voci",
  documenti: "Documenti (schede)",
  template_documenti: "Template documenti",
  modulo_configurazioni: "Configurazioni moduli",
  moduli_regionali_meta: "Moduli regionali",
  cantiere_entries: "Diario cantiere",
  cantiere_imprese: "Imprese in cantiere",
  pratica_cantiere_eventi: "Eventi cantiere",
  pratica_cantiere_foto: "Foto cantiere (schede)",
  pratica_preventivi_soggetti: "Soggetti preventivi",
};

export default function Backup() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/backup");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const creaOra = async () => {
    setCreating(true);
    try {
      await api.post("/backup");
      toast.success("Backup creato");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const scarica = async (b) => {
    try {
      await apiDownload(`/backup/${b.id}/download`, b.filename);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const elimina = async (b) => {
    if (!window.confirm(`Eliminare il backup del ${fmtDateTime(b.created_at)}?`)) return;
    try {
      await api.delete(`/backup/${b.id}`);
      toast.success("Backup eliminato");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const backups = data?.backups || [];
  const ultimo = data?.ultimo;

  return (
    <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-6 md:py-8" data-testid="backup-page">
      <PageHeader
        eyebrow="Sicurezza dati"
        title="Backup"
        description="Ogni giorno viene salvata automaticamente una copia dei tuoi dati. Puoi scaricarla quando vuoi."
        actions={
          <>
            <Button variant="outline" onClick={load} data-testid="backup-refresh-btn">
              <ArrowsClockwise size={16} className="mr-2" /> Aggiorna
            </Button>
            <Button onClick={creaOra} disabled={creating} data-testid="backup-create-btn">
              <ShieldCheck size={16} className="mr-2" />
              {creating ? "Creazione..." : "Crea backup adesso"}
            </Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="backup-stat-ultimo">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">
            <Clock size={13} /> Ultimo backup
          </div>
          <div className="font-heading text-xl font-bold text-slate-900">{ultimo ? fmtDateTime(ultimo.created_at) : "Nessuno"}</div>
          <div className="text-xs text-slate-500 mt-1">{ultimo ? fmtRelative(ultimo.created_at) : "Verrà creato automaticamente entro poco"}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="backup-stat-record">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">
            <Database size={13} /> Record salvati
          </div>
          <div className="font-heading text-xl font-bold text-slate-900">{ultimo ? ultimo.totale_record.toLocaleString("it-IT") : "—"}</div>
          <div className="text-xs text-slate-500 mt-1">{ultimo ? fmtSize(ultimo.size) : "in attesa del primo backup"}</div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-5" data-testid="backup-stat-copie">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-slate-400 mb-2">
            <ShieldCheck size={13} /> Copie disponibili
          </div>
          <div className="font-heading text-xl font-bold text-slate-900">{backups.length}</div>
          <div className="text-xs text-slate-500 mt-1">
            ultimi {data?.retention?.giornalieri || 7} giorni + 1 al mese per {data?.retention?.mensili || 12} mesi
          </div>
        </div>
      </div>

      <div className="rounded-lg border border-blue-100 bg-blue-50/60 px-5 py-4 mb-8 text-sm text-slate-700">
        Il backup contiene <strong>solo i dati</strong> (incarichi, clienti, imprese, preventivi, computi, fatture, voci di
        lavorazione, diario di cantiere…) in un file compresso leggero. I documenti e i PDF caricati non sono inclusi:
        restano nell'archivio Documenti.
      </div>

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 text-[10px] font-mono uppercase tracking-widest text-slate-400">
          Storico copie
        </div>
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-slate-400 font-mono">Caricamento...</div>
        ) : backups.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500" data-testid="backup-empty">
            Nessun backup ancora presente. Premi "Crea backup adesso" per generare subito la prima copia.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-slate-400 border-b border-slate-100">
                <th className="px-5 py-2.5 font-normal">Data e ora</th>
                <th className="px-3 py-2.5 font-normal">Tipo</th>
                <th className="px-3 py-2.5 font-normal text-right">Record</th>
                <th className="px-3 py-2.5 font-normal text-right">Dimensione</th>
                <th className="px-3 py-2.5 font-normal">Contenuto principale</th>
                <th className="px-5 py-2.5 font-normal text-right">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {backups.map((b) => {
                const top = Object.entries(b.conteggi || {})
                  .filter(([, v]) => v > 0)
                  .sort((a, c) => c[1] - a[1])
                  .slice(0, 3)
                  .map(([k, v]) => `${ETICHETTE[k] || k}: ${v}`)
                  .join(" · ");
                return (
                  <tr key={b.id} className="border-b border-slate-50 last:border-0 hover:bg-slate-50/60" data-testid={`backup-row-${b.id}`}>
                    <td className="px-5 py-3 font-medium text-slate-900 whitespace-nowrap">{fmtDateTime(b.created_at)}</td>
                    <td className="px-3 py-3">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                        b.tipo === "automatico" ? "bg-slate-900 text-white" : "bg-amber-100 text-amber-900"
                      }`}>
                        {b.tipo === "automatico" ? <Robot size={11} weight="fill" /> : <Hand size={11} weight="fill" />}
                        {b.tipo}
                      </span>
                    </td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-slate-600">{(b.totale_record || 0).toLocaleString("it-IT")}</td>
                    <td className="px-3 py-3 text-right font-mono text-xs text-slate-600">{fmtSize(b.size)}</td>
                    <td className="px-3 py-3 text-xs text-slate-500 truncate max-w-[320px]">{top || "—"}</td>
                    <td className="px-5 py-3 text-right whitespace-nowrap">
                      <button
                        type="button"
                        onClick={() => scarica(b)}
                        data-testid={`backup-download-${b.id}`}
                        title="Scarica"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-500 hover:bg-slate-900 hover:text-white transition-colors"
                      >
                        <DownloadSimple size={16} />
                      </button>
                      <button
                        type="button"
                        onClick={() => elimina(b)}
                        data-testid={`backup-delete-${b.id}`}
                        title="Elimina"
                        className="inline-flex items-center justify-center h-8 w-8 rounded-md text-slate-400 hover:bg-red-600 hover:text-white transition-colors ml-1"
                      >
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
